import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Monitor, Cpu, Shield, Video, Zap, Navigation, Package, Building2, Cog, Pause, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionSeparator from '../components/ui/SectionSeparator';
import { companyInfo, services, whyChooseUs, industries } from '../data/mock';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';

const iconMap = {
  Monitor, Cpu, Shield, Video, Zap, Navigation, Package, Building2, Cog
};

const Home = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef(null);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="SRAGCircle OPC | IT Systems Integration & Automation Solutions Philippines"
        description="SRAGCircle OPC is a SEC-registered Philippine corporation specializing in IT systems integration, SCADA automation, security surveillance, electrical power solutions, and facilities management services in Metro Manila."
        keywords="IT systems integrator Philippines, SCADA automation Metro Manila, security surveillance solutions, CCTV installation, electrical power equipment, facilities management, GPS fleet tracking, SRAGCircle OPC"
        canonical="/"
      />
      <Header />
      
      {/* Website Update Notice */}
      <div className="fixed top-[100px] md:top-[110px] lg:top-[120px] left-1/2 transform -translate-x-1/2 z-40">
        <div className="bg-[#D20000] text-white px-6 py-3 rounded-lg shadow-lg mb-8">
          <p className="text-sm md:text-base font-semibold whitespace-nowrap">
            WEBSITE UPDATE IN PROGRESS
          </p>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#0a0a1a] via-[#0d1a2d] to-[#0a0a1a] pt-16 md:pt-8">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0066CC]/20 rounded-full blur-[100px] animate-pulse-glow" />
          <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-[#00C0FF]/10 rounded-full blur-[80px] animate-float" />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-[#D20000]/5 rounded-full blur-[60px]" />
          {/* Grid Pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#0066CC]/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-fade-in">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                SEC-Registered Philippine Corporation
              </div>
              
              {/* Logo with blue light beam behind it */}
              <div className="relative mb-6 animate-fade-in-up flex justify-center lg:justify-start">
                <div className="absolute -inset-x-12 -inset-y-4 bg-gradient-to-r from-[#0066CC]/60 via-[#00C0FF]/80 to-[#0066CC]/60 rounded-full blur-[50px]" />
                <div className="absolute -inset-x-8 -inset-y-2 bg-[#00C0FF]/50 rounded-full blur-[30px]" />
                <img 
                  src="/srag-logo-only.png" 
                  alt="SRAGCircle OPC"
                  className="relative h-16 md:h-20 lg:h-24 w-auto object-contain"
                />
              </div>
              
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-gray-300 mb-6 animate-fade-in-up">
                Systems, Resources & Automations Group
              </h2>
              
              <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed animate-fade-in-up animation-delay-200">
                {companyInfo.description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-300">
                <Link to="/about">
                  <Button className="bg-[#0066CC] hover:bg-[#0055AA] text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg shadow-[#0066CC]/30 hover:shadow-xl hover:shadow-[#0066CC]/40 transition-all duration-300 w-full sm:w-52">
                    About Us
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="bg-[#D20000] hover:bg-[#B00000] text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg shadow-[#D20000]/30 hover:shadow-xl hover:shadow-[#D20000]/40 transition-all duration-300 w-full sm:w-52">
                    Request a Proposal
                  </Button>
                </Link>
              </div>
            </div>

            {/* Bar and Sphere Container */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8">
              {/* Vertical Bar Separator - Desktop only */}
              <div className="hidden lg:flex items-center justify-center flex-shrink-0 relative z-10">
                <div className="w-[12px] h-[500px] bg-black shadow-none" style={{ backgroundColor: '#000000', boxShadow: 'none' }} />
              </div>

              {/* Hero Video/Visual - Visible on all devices */}
              <div className="flex justify-center items-center animate-fade-in animation-delay-400 lg:pl-12">
                <div className="relative flex items-center justify-center w-[240px] h-[240px] sm:w-[320px] sm:h-[320px] md:w-[420px] md:h-[420px] lg:w-[500px] lg:h-[500px]">
                {/* Wider blue light beam effect - behind video */}
                <div className="absolute -inset-16 sm:-inset-20 lg:-inset-32 bg-gradient-to-r from-[#0066CC]/50 via-[#00C0FF]/60 to-[#0066CC]/50 rounded-full blur-[100px] sm:blur-[140px] lg:blur-[180px] animate-pulse-glow" />
                <div className="absolute -inset-10 sm:-inset-14 lg:-inset-20 bg-gradient-to-br from-[#00C0FF]/40 to-[#0066CC]/40 rounded-full blur-[80px] sm:blur-[100px] lg:blur-[120px]" />
                <div className="absolute -inset-6 sm:-inset-8 lg:-inset-10 bg-[#00C0FF]/25 rounded-full blur-[50px] sm:blur-[60px] lg:blur-[80px]" />
                
                {/* Video container with rounded corners and overflow hidden */}
                <div 
                  className="relative w-full h-full rounded-full overflow-hidden cursor-pointer group"
                  onClick={toggleVideo}
                  title={isPlaying ? "Click to pause" : "Click to play"}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    className="scale-125"
                    src="/globe-video.mp4"
                  />
                  
                  {/* Play/Pause overlay indicator */}
                  <div className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      {isPlaying ? (
                        <Pause className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                      ) : (
                        <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Soft edge vignette overlay */}
                <div 
                  className="absolute inset-0 z-20 pointer-events-none rounded-full"
                  style={{
                    background: 'radial-gradient(circle at center, transparent 35%, rgba(10, 10, 26, 0.3) 55%, rgba(10, 10, 26, 0.7) 70%, rgba(10, 10, 26, 0.95) 85%)',
                    boxShadow: 'inset 0 0 100px 50px rgba(10, 10, 26, 0.8)'
                  }}
                />
                
                {/* Light beam glow overlay on top */}
                <div 
                  className="absolute inset-0 z-30 pointer-events-none rounded-full"
                  style={{
                    background: 'radial-gradient(circle at center, rgba(0, 192, 255, 0.2) 0%, rgba(0, 102, 204, 0.15) 25%, transparent 55%)'
                  }}
                />
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0066CC] font-semibold text-sm tracking-wider uppercase">Our Expertise</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a5c] mt-3 mb-4">
              What We Do
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Comprehensive solutions spanning IT, automation, security, and facilities management
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-[#0066CC]/30 transition-all duration-300 animate-fade-in-up cursor-default"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#0066CC]/10 to-[#00C0FF]/10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    {Icon && <Icon className="w-7 h-7 text-[#0066CC]" />}
                  </div>
                  <h3 className="font-semibold text-[#1a1a5c] mb-2 group-hover:text-[#0066CC] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {service.shortDescription}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="border-[#0066CC] text-[#0066CC] hover:bg-[#0066CC] hover:text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300">
                View All Products & Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Separator */}
        <SectionSeparator className="mt-16" />
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#0a0a1a] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-0 w-96 h-96 bg-[#0066CC]/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-[#00C0FF]/5 rounded-full blur-[80px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#00C0FF] font-semibold text-sm tracking-wider uppercase">Why SRAGCircle OPC</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-6">
                Your Trusted Partner for
                <span className="text-[#0066CC]"> Integrated Solutions</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                With over 35 years of combined expertise, we deliver end-to-end solutions
                that drive efficiency, security, and growth for your organization.
              </p>
              <div className="space-y-5">
                {whyChooseUs.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-4 animate-fade-in-up"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="w-10 h-10 bg-[#0066CC]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-[#00C0FF]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Card */}
            <div className="relative">
              <div className="glass rounded-3xl p-8 md:p-10">
                <div className="grid grid-cols-2 gap-8">
                  <div className="text-center p-6 bg-white/5 rounded-2xl">
                    <div className="text-4xl md:text-5xl font-bold text-[#0066CC] mb-2">35+</div>
                    <div className="text-gray-400 text-sm">Years Combined Experience</div>
                  </div>
                  <div className="text-center p-6 bg-white/5 rounded-2xl">
                    <div className="text-4xl md:text-5xl font-bold text-[#00C0FF] mb-2">9</div>
                    <div className="text-gray-400 text-sm">Service Categories</div>
                  </div>
                  <div className="text-center p-6 bg-white/5 rounded-2xl">
                    <div className="text-4xl md:text-5xl font-bold text-[#D20000] mb-2">24/7</div>
                    <div className="text-gray-400 text-sm">Support Available</div>
                  </div>
                  <div className="text-center p-6 bg-white/5 rounded-2xl">
                    <div className="text-4xl md:text-5xl font-bold text-white mb-2">SEC</div>
                    <div className="text-gray-400 text-sm">Registered Corporation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0066CC] font-semibold text-sm tracking-wider uppercase">Industries We Serve</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a5c] mt-3 mb-4">
              Who We Work With
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Serving diverse sectors with tailored IT, security, power, and facilities solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <div
                key={industry.id}
                className="group relative p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl hover:from-[#0066CC] hover:to-[#004499] transition-all duration-500 cursor-pointer overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#00C0FF]/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <h3 className="text-lg font-semibold text-[#1a1a5c] group-hover:text-white mb-3 transition-colors duration-300">
                  {industry.title}
                </h3>
                <p className="text-gray-600 text-sm group-hover:text-white/80 transition-colors duration-300">
                  {industry.description}
                </p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Separator */}
        <SectionSeparator className="mt-16" />
      </section>

      {/* LexDoRight App Development Portfolio */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gray-100">
            <div className="flex-1">
              <span className="text-[#0066CC] font-semibold text-sm tracking-wider uppercase">App Development Portfolio</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a5c] mt-3 mb-4">
                LexDoRight
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                LexDoRight serves as SRAGCircle OPC's app development portfolio, showcasing our technology-driven solutions and digital products. Explore our innovative applications and custom software solutions.
              </p>
              <a
                href="https://lexdoright.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0066CC] hover:bg-[#0055AA] text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg"
              >
                Visit LexDoRight
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="flex-shrink-0">
              <a
                href="https://lexdoright.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block transition-transform duration-300 hover:scale-105"
              >
                <div className="p-4 bg-[#5C1A1A] rounded-2xl shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_sragcorp/artifacts/zxd3dxir_LDR_Logo_only.png"
                    alt="LexDoRight"
                    className="h-32 md:h-40 w-auto object-contain"
                  />
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0066CC] to-[#004499] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-[60px]" />
          <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-[#00C0FF]/20 rounded-full blur-[40px]" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Operations?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Let's discuss how SRAGCircle OPC can deliver integrated solutions
            tailored to your organization's needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-[#0066CC] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl shadow-xl transition-all duration-300">
                Request a Proposal
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-300">
                Learn More About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
